/**
 * Shared per-element WORLD FOOTPRINT + eligibility check for the per-element-move
 * batch ops (Distribute, Align — docs/MODELER.md "DISTRIBUTE round" backlog: "The
 * Align family noted above"). Both derive from the SAME per-category plan-pick body
 * registry marquee-select already uses (`pickConfigFor(category).body(el, model)` in
 * `editing/pickConfigs.js`), so a beam/wall/column/casework/object/… all reduce to the
 * same "position" concept with zero per-category code — this module just adds the
 * bbox extents (min/max) that Align needs on top of Distribute's bbox CENTER.
 */
import { pickConfigFor } from './pickConfigs.js'

/**
 * True if the element has independent world position to move — freestanding, or
 * hosted but carrying its OWN path (a ceiling-hosted light: recessedCan/pendant/
 * flushMount use the object anchor model — `path`/`rotationDeg` — with a `hostId`
 * only for cascade-delete/derived-height, per the CEILING HOSTING round). False for
 * door/window/wall-fixture (outlet/switch/sconce/lowVoltage) — their position is
 * DERIVED from the host via hostOffsetFt, so they correctly ride the host instead.
 * Mirrors `elementCommands.hasOwnPath`'s convention
 * (`packages/bim-kernel/src/commands/elements.ts`, already applied to
 * translateSelection/mirrorSelection) — kept as a small standalone check here rather
 * than importing the kernel's private helper, since the frontend editing layer
 * already owns its own copy of every other per-category fact in this file's sibling
 * `pickConfigs.js`.
 */
export function hasIndependentPosition(el) {
  return !el.hostId || !!el.params?.path?.nodes?.length
}

/**
 * One world-space footprint per element: `{ minX, minY, maxX, maxY, cx, cy }`, or
 * null if the category has no plan-pick body (nothing to anchor on) or the element's
 * own body can't currently be derived (e.g. a column mid-placement with no anchor
 * point yet). `cx`/`cy` are the bbox center — Distribute's anchor point unchanged
 * from before this file existed; `minX..maxY` are new, for Align's edge modes.
 *
 * A corridor/beam/wall's true rotated extent isn't recomputed here — like Distribute
 * already did, the raw point bbox (ignoring width) is used as the v1 approximation.
 * A 'spot' (e.g. a round column) expands by its radius so it isn't reduced to a
 * zero-size point.
 */
export function elementFootprint(model, el) {
  const cfg = pickConfigFor(el.category)
  const body = cfg ? cfg.body(el, model) : null
  if (!body) return null
  if (body.kind === 'spot') {
    const r = body.rFt || 0
    const [x, y] = body.pt
    return { minX: x - r, maxX: x + r, minY: y - r, maxY: y + r, cx: x, cy: y }
  }
  if (body.pts && body.pts.length) {
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity
    for (const [x, y] of body.pts) {
      if (x < minX) minX = x; if (x > maxX) maxX = x
      if (y < minY) minY = y; if (y > maxY) maxY = y
    }
    return { minX, minY, maxX, maxY, cx: (minX + maxX) / 2, cy: (minY + maxY) / 2 }
  }
  return null
}
